import {
  Button,
  Modal,
  Select,
  SelectOptionType,
  TextInput,
  addToast,
  useMobile,
} from '@octano/global-ui';
import { ReactElement, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useActionKitImp } from '../../../../components/ozmoit/common';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  createCourseGroupAction,
  getSchoolsListAction,
  updateCourseGroupAction,
} from '../actions';
import { CourseGroupDetail, CreateCourseGroupBody } from '../types';
import { extractBackendErrorCode } from '../utils/extractBackendErrorCode';

interface CreateCourseGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (created?: CourseGroupDetail) => void;
  courseGroup?: CourseGroupDetail | null;
}

interface FormValues {
  groupName: string;
  groupCode: string;
  school: SelectOptionType | null;
}

const defaultValues: FormValues = {
  groupName: '',
  groupCode: '',
  school: null,
};

/**
 * HU 15565 — Modal de creación/edición de Agrupaciones de actividades.
 *
 * Sigue el patrón de los modales del repo (`InstitutionsModal`,
 * `AdditionalChargeModal`): usa `<Modal>` de `@octano/global-ui` con
 * layout `Container > Row/Col` adentro y `<h1>` centrado como título.
 * Botones Cancelar / Guardar van apilados en mobile (Cancelar abajo,
 * Guardar arriba) y lado a lado en desktop (Cancelar izquierda).
 *
 * Branchea en COURSE_GROUP_CODE_TAKEN usando extractBackendErrorCode.
 */
export default function CreateCourseGroupModal({
  isOpen,
  onClose,
  onSuccess,
  courseGroup,
}: CreateCourseGroupModalProps): ReactElement {
  const { t } = useTranslation(TranslationsKeys.COURSE_GROUPS);
  const { msgValidations } = useValidations();
  const history = useHistory();
  const isMobile = useMobile();
  const isEdit = Boolean(courseGroup);
  const [schools, setSchools] = useState<SelectOptionType[]>([]);

  const methods = useForm<FormValues>({
    defaultValues,
  });

  const { control, handleSubmit, reset } = methods;
  const { action: loadSchools } = useActionKitImp(getSchoolsListAction, {
    onSuccess: (resp) => {
      setSchools(
        (resp?.data ?? []).map((school) => ({
          value: school.id,
          label: school.name,
        })),
      );
    },
    onError: {
      unknown: () => {
        addToast({
          color: 'danger',
          icon: 'error',
          text: t('errors.generic'),
        });
      },
    },
  });

  // Reset form al abrir y cuando cambia el courseGroup que se está editando.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    loadSchools();
    reset({
      groupName: courseGroup?.name ?? '',
      groupCode: courseGroup?.code ?? '',
      school: courseGroup?.school
        ? { value: courseGroup.school.id, label: courseGroup.school.name }
        : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, courseGroup]);

  const handleMutationError = (error: unknown): void => {
    const code = extractBackendErrorCode(error);
    const text =
      code === 'COURSE_GROUP_CODE_TAKEN'
        ? t('errors.codeTaken')
        : t('errors.generic');
    addToast({ icon: 'error', color: 'danger', text });
  };

  const { action: createAction, loading: creating } = useActionKitImp(
    createCourseGroupAction,
    {
      onSuccess: (created) => {
        onClose();
        onSuccess(created);
        history.push(
          `/academic-offers/course-groups/${created.id}/items?created=1`,
        );
      },
      onError: {
        unknown: handleMutationError,
      },
    },
  );

  const { action: updateAction, loading: updating } = useActionKitImp(
    updateCourseGroupAction,
    {
      onSuccess: (updated) => {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('createModal.editSuccess'),
        });
        onClose();
        onSuccess(updated);
      },
      onError: {
        unknown: handleMutationError,
      },
    },
  );

  const onSubmitForm = (values: FormValues): void => {
    const payload: CreateCourseGroupBody = {
      name: values.groupName.trim(),
      code: values.groupCode.trim(),
      schoolId: values.school ? Number(values.school.value) : null,
    };
    if (isEdit && courseGroup) {
      updateAction(courseGroup.id, payload);
    } else {
      createAction(payload);
    }
  };

  const isLoading = creating || updating;

  if (!isOpen) {
    return <></>;
  }

  return (
    <Modal isOpen={true} toggle={onClose} closeOnBackdrop={false}>
      <div>
        <h1 className="text-dark text-center fw-600 fs-20">
          {isEdit ? t('createModal.editTitle') : t('createModal.title')}
        </h1>
        <TextInput
          name="groupName"
          label={t('createModal.fields.name')}
          control={control}
          rules={{
            required: msgValidations.required,
            maxLength: {
              message: msgValidations.invalidMaxLength(255),
              value: 255,
            },
          }}
        />
        <TextInput
          name="groupCode"
          label={t('createModal.fields.code')}
          control={control}
          rules={{
            required: msgValidations.required,
            maxLength: {
              message: msgValidations.invalidMaxLength(255),
              value: 255,
            },
          }}
        />
        <Select
          name="school"
          label={t('createModal.fields.responsibleSchool')}
          placeholder={t('createModal.fields.selectOption')}
          control={control}
          options={schools}
          rules={{ required: msgValidations.required }}
        />
        <div
          className="d-flex flex-wrap justify-content-between mt-4"
          style={{ gap: '1rem' }}
        >
          <Button
            type="button"
            text={t('createModal.actions.cancel')}
            outlined
            style={{ width: isMobile ? '100%' : 197 }}
            disabled={isLoading}
            onClick={onClose}
          />
          <Button
            type="button"
            text={t('createModal.actions.confirm')}
            style={{ width: isMobile ? '100%' : 197 }}
            loading={isLoading}
            onClick={handleSubmit(onSubmitForm)}
          />
        </div>
      </div>
    </Modal>
  );
}
